/**
 * Rate Review Table Component
 */
import React, {
  memo,
  useMemo,
  useEffect,
  useRef,
} from "react";
import TableSkeleton from "../../../../shared/components/TableSkeleton/TableSkeleton";
import RateReviewTableBody from "./RateReviewTableBody";
import SortableHeader from "../../../../shared/components/Table/SortableHeader";
import ResultsLoadMorePanel from "../../../../shared/components/ResultsLoadMorePanel/ResultsLoadMorePanel";
import "../../../../shared/components/Table/Table.scss";

const RateReviewTable = memo(
  ({
    leads = [],
    headers = [],
    isLoading = false,
    onLeadClick,
    onSort = null,
    sortColumn = null,
    sortDirection = null,
    secondarySortColumn = null,
    secondarySortDirection = null,
    rows = 50,
    isLoadingMore = false,
    hasMore = false,
    onLoadMore = null,
    hideFooter = false,
    footerLoadingState = isLoading,
    disableLoadMoreButton = false,
    canEdit = true,
    filterRow = null, // New prop for filter row
  }) => {
    const tableRef = useRef(null);
    const theadRef = useRef(null);
    const tbodyRef = useRef(null);

    const headerRows = useMemo(
      () => (
        <tr>
          {headers.map((header, index) => {
            const isSortable = header === "Date Submitted";
            const currentSortDirection = sortColumn === index ? sortDirection : null;
            
            return (
              <SortableHeader
                key={`header-${header}-${index}`}
                header={header}
                isSortable={isSortable}
                sortDirection={currentSortDirection}
                secondarySortDirection={index === secondarySortColumn ? secondarySortDirection : null}
                onSort={onSort}
                columnIndex={index}
              />
            );
          })}
        </tr>
      ),
      [headers, sortColumn, sortDirection, secondarySortColumn, secondarySortDirection, onSort]
    );

    useEffect(() => {
      const table = tableRef.current;
      const thead = theadRef.current;

      if (!table || !thead) return;

      const handleStickyScroll = () => {
        const tableRect = table.getBoundingClientRect();
        
        if (tableRect.top <= 0) {
          thead.classList.add("scrolling");
        } else {
          thead.classList.remove("scrolling");
        }
      };

      window.addEventListener("scroll", handleStickyScroll);
      return () => window.removeEventListener("scroll", handleStickyScroll);
    }, [leads.length]);

    return (
      <div className="table-wrapper rate-review-table merchant-list-view">
        <div className="table-responsive custom-table position-relative business-app-table">
          <table ref={tableRef} className="table table-sticky-header table-hover">
            <colgroup>
              {headers.map((_, index) => (
                <col key={`col-${index}`} style={{ width: `${100 / headers.length}%` }} />
              ))}
            </colgroup>
            {filterRow && (
              <thead className="filter-header">
                {filterRow}
              </thead>
            )}
            <thead ref={theadRef} className="sticky-header">
              {/* Header Row */}
              {headerRows}
            </thead>
            {/* Show skeleton loader only in tbody during loading - keep showing until loading is complete */}
            {isLoading ? (
              <TableSkeleton
                rows={50}
                columns={headers.length}
                tbodyOnly={true}
              />
            ) : (
              <RateReviewTableBody
                leads={leads}
                headers={headers}
                onLeadClick={onLeadClick}
                tbodyRef={tbodyRef}
                canEdit={canEdit}
              />
            )}

            {/* Infinite scroll skeleton loader - shown as tbody */}
            {isLoadingMore && (
              <TableSkeleton
                columns={headers.length}
                rows={5}
                infiniteScroll={true}
              />
            )}

          </table>
        </div>

        {/* Load More Results Button - show only if there are leads and not loading */}
        {!hideFooter && (
          <ResultsLoadMorePanel
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={(shouldScroll) => {
              if (onLoadMore) {
                onLoadMore();
                // Auto scroll down to show loading area if shouldScroll is true
                if (shouldScroll) {
                  setTimeout(() => {
                    window.scrollTo({
                      top: document.documentElement.scrollHeight,
                      behavior: 'smooth'
                    });
                  }, 100);
                }
              }
            }}
            loading={footerLoadingState}
            itemsCount={leads.length}
            shouldScroll={true}
            forceDisableButton={disableLoadMoreButton}
          />
        )}
      </div>
    );
  }
);

RateReviewTable.displayName = "RateReviewTable";

export default RateReviewTable;

